/**
 * LLMVisibilityPromoBlock
 * Premium module promoting the free LLM Visibility Checker tool.
 * Used on: Homepage, AI SEO service page, AI SEO blog posts.
 *
 * size="full"    — two-column hero section (homepage / AI SEO page)
 * size="compact" — inline blog CTA strip
 */
import React, { useState } from "react";
import { useIsMobile } from "@/hooks/useIsMobile";

const ff = "'Barlow', 'Barlow Fallback', sans-serif";
const cyan = "#57d7e2";

// ── Tiny engine logos (SVG paths) ──────────────────────────────────────
const ChatGPTIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
    <path d="M22.2 12c0-5.5-4.5-10-10-10a10 10 0 00-9.8 8.1C1.2 11.3 1 12.5 1 13.7c0 3.3 2.7 6 6 6h9.5c3.2 0 5.7-2.6 5.7-5.7z" stroke="rgba(255,255,255,0.7)" strokeWidth="1.4"/>
    <path d="M8 12h8M8 8.5h5M8 15.5h6" stroke="rgba(255,255,255,0.5)" strokeWidth="1.3" strokeLinecap="round"/>
  </svg>
);
const PerplexityIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="12" r="9" stroke="rgba(255,255,255,0.7)" strokeWidth="1.4"/>
    <path d="M12 7v5l3 3" stroke="rgba(255,255,255,0.6)" strokeWidth="1.4" strokeLinecap="round"/>
    <circle cx="12" cy="12" r="2" fill="rgba(255,255,255,0.4)"/>
  </svg>
);
const GoogleAIIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
    <path d="M12 3C7 3 3 7 3 12s4 9 9 9 9-4 9-9" stroke="rgba(255,255,255,0.7)" strokeWidth="1.4" strokeLinecap="round"/>
    <path d="M17 3l4 4-4 4" stroke="rgba(255,255,255,0.55)" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M9 12l2 2 4-4" stroke="rgba(255,255,255,0.5)" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const GeminiIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
    <path d="M12 2l2.5 7.5H22l-6.5 4.5 2.5 7.5L12 17l-6 4.5 2.5-7.5L3 9.5h7.5z" stroke="rgba(255,255,255,0.7)" strokeWidth="1.3" strokeLinejoin="round"/>
  </svg>
);

// ── Simulated report mockup ─────────────────────────────────────────────
function ReportMockup() {
  const engines = [
    { icon: <ChatGPTIcon />, name: "ChatGPT", mentioned: true },
    { icon: <PerplexityIcon />, name: "Perplexity", mentioned: false },
    { icon: <GoogleAIIcon />, name: "Google AI", mentioned: true },
    { icon: <GeminiIcon />, name: "Gemini", mentioned: false },
  ];
  const score = 2;
  const max = 3;
  const pct = Math.round((score / max) * 100);

  return (
    <div style={{
      background: "linear-gradient(145deg, #0d1a1c 0%, #091215 100%)",
      border: "1px solid rgba(87,215,226,0.18)",
      borderRadius: "20px",
      overflow: "hidden",
      boxShadow: "0 40px 80px rgba(0,0,0,0.5), 0 0 0 1px rgba(87,215,226,0.06), inset 0 1px 0 rgba(87,215,226,0.08)",
      width: "100%",
      maxWidth: "420px",
      flexShrink: 0,
    }}>
      {/* Top accent */}
      <div style={{ height: "3px", background: `linear-gradient(90deg, ${cyan}, #3a9da6 60%, transparent)` }} />

      {/* Header */}
      <div style={{ padding: "20px 24px 16px", borderBottom: "1px solid rgba(255,255,255,0.06)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div>
          <div style={{ fontFamily: ff, fontSize: "11px", fontWeight: 700, letterSpacing: "0.1em", color: "rgba(87,215,226,0.8)", textTransform: "uppercase" as const, marginBottom: "4px" }}>
            AI Visibility Report
          </div>
          <div style={{ fontFamily: ff, fontSize: "15px", fontWeight: 700, color: "white" }}>acme.com</div>
        </div>
        <div style={{
          background: "rgba(87,215,226,0.1)", border: "1px solid rgba(87,215,226,0.2)",
          borderRadius: "8px", padding: "6px 12px",
          fontFamily: ff, fontSize: "11px", fontWeight: 700, color: cyan,
          letterSpacing: "0.05em",
        }}>
          AI Tool
        </div>
      </div>

      {/* Score */}
      <div style={{ padding: "20px 24px 16px", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
        <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", marginBottom: "10px" }}>
          <div>
            <div style={{ fontFamily: ff, fontSize: "11px", fontWeight: 600, color: "rgba(255,255,255,0.4)", letterSpacing: "0.07em", textTransform: "uppercase" as const, marginBottom: "4px" }}>
              Visibility Score
            </div>
            <div style={{ display: "flex", alignItems: "baseline", gap: "4px" }}>
              <span style={{ fontFamily: ff, fontSize: "42px", fontWeight: 800, color: cyan, lineHeight: 1, letterSpacing: "-0.03em" }}>{score}</span>
              <span style={{ fontFamily: ff, fontSize: "18px", color: "rgba(255,255,255,0.25)", fontWeight: 500 }}>/{max}</span>
            </div>
          </div>
          <div style={{ textAlign: "right" as const }}>
            <div style={{ fontFamily: ff, fontSize: "11px", color: "rgba(255,255,255,0.35)", marginBottom: "4px" }}>AI engines checked</div>
            <div style={{ fontFamily: ff, fontSize: "22px", fontWeight: 700, color: "rgba(255,255,255,0.7)" }}>4</div>
          </div>
        </div>
        {/* Score bar */}
        <div style={{ background: "rgba(255,255,255,0.06)", borderRadius: "4px", height: "6px", overflow: "hidden" }}>
          <div style={{
            width: `${pct}%`, height: "100%", borderRadius: "4px",
            background: `linear-gradient(90deg, ${cyan}, #4ade80)`,
            transition: "width 1s ease",
          }} />
        </div>
      </div>

      {/* Engine results */}
      <div style={{ padding: "16px 24px 20px", display: "flex", flexDirection: "column", gap: "10px" }}>
        <div style={{ fontFamily: ff, fontSize: "11px", fontWeight: 600, color: "rgba(255,255,255,0.3)", letterSpacing: "0.07em", textTransform: "uppercase" as const, marginBottom: "4px" }}>
          Per-engine results
        </div>
        {engines.map(({ icon, name, mentioned }) => (
          <div key={name} style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            padding: "9px 12px", borderRadius: "10px",
            background: mentioned ? "rgba(87,215,226,0.05)" : "rgba(255,255,255,0.025)",
            border: `1px solid ${mentioned ? "rgba(87,215,226,0.15)" : "rgba(255,255,255,0.06)"}`,
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <div style={{
                width: "28px", height: "28px", borderRadius: "8px",
                background: "rgba(255,255,255,0.05)",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>{icon}</div>
              <span style={{ fontFamily: ff, fontSize: "13px", fontWeight: 600, color: "rgba(255,255,255,0.8)" }}>{name}</span>
            </div>
            <div style={{
              display: "flex", alignItems: "center", gap: "5px",
              padding: "3px 10px", borderRadius: "20px",
              background: mentioned ? "rgba(74,222,128,0.12)" : "rgba(248,113,113,0.1)",
              border: `1px solid ${mentioned ? "rgba(74,222,128,0.25)" : "rgba(248,113,113,0.2)"}`,
            }}>
              <span style={{ fontSize: "9px" }}>{mentioned ? "●" : "○"}</span>
              <span style={{ fontFamily: ff, fontSize: "11px", fontWeight: 700, color: mentioned ? "#4ade80" : "#f87171" }}>
                {mentioned ? "Mentioned" : "Not found"}
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* Bottom CTA teaser */}
      <div style={{
        margin: "0 16px 16px", padding: "12px 16px", borderRadius: "12px",
        background: "rgba(87,215,226,0.07)", border: "1px solid rgba(87,215,226,0.15)",
        display: "flex", alignItems: "center", gap: "10px",
      }}>
        <div style={{
          width: "32px", height: "32px", borderRadius: "8px", flexShrink: 0,
          background: "rgba(87,215,226,0.12)", border: "1px solid rgba(87,215,226,0.2)",
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
            <path d="M8 2v4M8 10v4M2 8h4M10 8h4" stroke={cyan} strokeWidth="1.5" strokeLinecap="round"/>
            <circle cx="8" cy="8" r="2.5" stroke={cyan} strokeWidth="1.3"/>
          </svg>
        </div>
        <div>
          <div style={{ fontFamily: ff, fontSize: "12px", fontWeight: 700, color: "white", marginBottom: "1px" }}>
            Full report includes action plan
          </div>
          <div style={{ fontFamily: ff, fontSize: "11px", color: "rgba(255,255,255,0.4)" }}>
            Why you're missing + how to fix it
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Props ───────────────────────────────────────────────────────────────
interface Props {
  /** "full" = large two-column section | "compact" = inline blog strip */
  size?: "full" | "compact";
  /** Override background color */
  bg?: string;
}

export default function LLMVisibilityPromoBlock({ size = "full", bg }: Props) {
  const isMobile = useIsMobile();
  const [hovCta, setHovCta] = useState(false);

  // ── Compact version (blog/article inline) ──────────────────────────
  if (size === "compact") {
    return (
      <div style={{
        margin: "40px 0",
        background: bg ?? "linear-gradient(135deg, rgba(87,215,226,0.06) 0%, rgba(13,26,28,1) 100%)",
        border: "1px solid rgba(87,215,226,0.2)",
        borderRadius: "16px",
        padding: "26px 28px",
        display: "flex",
        flexDirection: "column",
        gap: "20px",
      }}>
        {/* Heading row: icon + copy */}
        <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
          {/* Icon */}
          <div style={{
            width: "44px", height: "44px", borderRadius: "12px", flexShrink: 0,
            background: "rgba(87,215,226,0.1)", border: "1px solid rgba(87,215,226,0.2)",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="9" stroke={cyan} strokeWidth="1.5"/>
              <path d="M8 12l3 3 5-5" stroke={cyan} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          {/* Copy */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontFamily: ff, fontSize: "16px", fontWeight: 700, color: "white", marginBottom: "4px", lineHeight: 1.3 }}>
              Is your brand showing up in AI search?
            </div>
            <div style={{ fontFamily: ff, fontSize: "13px", color: "rgba(255,255,255,0.5)", lineHeight: 1.5 }}>
              Check your visibility across ChatGPT, Perplexity, Google AI Overviews &amp; Gemini in under 2 minutes.
            </div>
          </div>
        </div>
        {/* CTA */}
        <a
          href="/tools/llm-visibility-checker"
          onMouseEnter={() => setHovCta(true)}
          onMouseLeave={() => setHovCta(false)}
          style={{
            alignSelf: "flex-start",
            display: "inline-flex", alignItems: "center", gap: "7px",
            padding: "12px 22px", borderRadius: "88px",
            background: hovCta
              ? "linear-gradient(135deg, #9ae8ee 0%, #2abbc8 100%)"
              : "linear-gradient(135deg, #85e2e9 0%, #20b0bc 100%)",
            border: `1px solid ${cyan}`,
            boxShadow: hovCta ? "0 4px 20px rgba(87,215,226,0.35)" : "0 2px 12px rgba(87,215,226,0.2)",
            color: "#0a0c0c", fontFamily: ff, fontSize: "14px", fontWeight: 700,
            textDecoration: "none", whiteSpace: "nowrap" as const,
            transition: "all 0.2s",
          }}
        >
          Check your visibility
          <svg width="12" height="12" viewBox="0 0 14 14" fill="none">
            <path d="M3 11L11 3M11 3H5M11 3v6" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </a>
      </div>
    );
  }

  // ── Full version (homepage / service page) ─────────────────────────
  return (
    <section className="section-grid" style={{
      background: bg ?? "#080c0e",
      borderTop: "1px solid rgba(87,215,226,0.08)",
      borderBottom: "1px solid rgba(87,215,226,0.08)",
      position: "relative",
      overflow: "hidden",
    }}>
      {/* Ambient glow */}
      <div style={{
        position: "absolute", top: "-120px", left: "50%", transform: "translateX(-50%)",
        width: "600px", height: "300px",
        background: "#3aefff", filter: "blur(180px)", opacity: 0.06,
        pointerEvents: "none",
      }} />
      <div style={{
        position: "absolute", bottom: "-80px", right: "-60px",
        width: "400px", height: "300px",
        background: "#3aefff", filter: "blur(160px)", opacity: 0.04,
        pointerEvents: "none",
      }} />

      <div style={{
        maxWidth: "1200px", margin: "0 auto",
        padding: isMobile ? "64px 20px" : "96px 72px",
        display: "flex",
        flexDirection: isMobile ? "column" : "row" as const,
        alignItems: "center",
        gap: isMobile ? "48px" : "80px",
        position: "relative",
      }}>
        {/* ── LEFT: Copy ── */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Headline */}
          <h2 style={{
            fontFamily: ff, fontWeight: 800,
            fontSize: isMobile ? "32px" : "clamp(36px, 4vw, 52px)",
            lineHeight: 1.08, letterSpacing: "-0.03em",
            color: "white", margin: "0 0 20px",
          }}>
            Does AI recommend<br />
            <span style={{ color: cyan }}>your business?</span>
          </h2>

          {/* Sub-copy */}
          <p style={{
            fontFamily: ff, fontSize: isMobile ? "16px" : "18px",
            color: "rgba(255,255,255,0.55)", lineHeight: 1.75,
            margin: "0 0 36px", maxWidth: "480px",
          }}>
            ChatGPT, Perplexity, Google AI Overviews, and Gemini are now how
            millions of buyers research and shortlist vendors. Our tool
            shows you exactly where you stand — in under 2 minutes.
          </p>

          {/* Feature bullets */}
          <div style={{ display: "flex", flexDirection: "column", gap: "12px", marginBottom: "40px" }}>
            {[
              "Checks all 4 major AI search engines",
              "Identifies what's stopping you from being cited",
              "Personalized action plan to improve visibility",
            ].map((point) => (
              <div key={point} style={{ display: "flex", alignItems: "flex-start", gap: "10px" }}>
                <div style={{
                  marginTop: "3px", width: "16px", height: "16px", borderRadius: "50%", flexShrink: 0,
                  background: "rgba(87,215,226,0.12)", border: "1px solid rgba(87,215,226,0.3)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <svg width="8" height="8" viewBox="0 0 10 10" fill="none">
                    <path d="M2 5l2.5 2.5L8 3" stroke={cyan} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <span style={{ fontFamily: ff, fontSize: "15px", color: "rgba(255,255,255,0.7)", lineHeight: 1.5 }}>{point}</span>
              </div>
            ))}
          </div>

          {/* CTA */}
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: "12px" }}>
            <a
              href="/tools/llm-visibility-checker"
              onMouseEnter={() => setHovCta(true)}
              onMouseLeave={() => setHovCta(false)}
              style={{
                display: "inline-flex", alignItems: "center", gap: "8px",
                padding: "16px 32px", borderRadius: "88px",
                background: hovCta
                  ? "linear-gradient(135deg, #9ae8ee 0%, #2abbc8 100%)"
                  : "linear-gradient(135deg, #85e2e9 0%, #20b0bc 100%)",
                border: `1px solid ${cyan}`,
                boxShadow: hovCta
                  ? "0 6px 32px rgba(87,215,226,0.4)"
                  : "0 2px 15px rgba(87,215,226,0.2)",
                color: "#0a0c0c", fontFamily: ff,
                fontSize: isMobile ? "15px" : "16px", fontWeight: 700,
                textDecoration: "none",
                transition: "all 0.2s",
                transform: hovCta ? "translateY(-1px)" : "translateY(0)",
              }}
            >
              Check your AI visibility
              <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
                <path d="M3 11L11 3M11 3H5M11 3v6" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </a>
            <span style={{ fontFamily: ff, fontSize: "13px", color: "rgba(255,255,255,0.3)" }}>
              No account needed · Results in 2 minutes
            </span>
          </div>
        </div>

        {/* ── RIGHT: Mockup ── */}
        <div style={{
          flexShrink: 0,
          width: isMobile ? "100%" : "420px",
          display: "flex",
          justifyContent: "center",
        }}>
          <ReportMockup />
        </div>
      </div>
    </section>
  );
}
